import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PropTypes from 'prop-types';
import { fetchCompanyUsers } from '../../redux/slices/companySlice';
import UserDetailsModal from './UserDetailsModal';
// Tip değiştir özelliği kaldırıldı
import UserFormModal from './UserFormModal';
import DeleteUserDialog from './DeleteUserDialog';
import PermissionGate from '../common/PermissionGate';

const UserTable = ({ users, total, page, limit, loading }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  // const [userTypeModalOpen, setUserTypeModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  
  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleViewDetails = () => {
    handleMenuClose();
    setDetailsModalOpen(true);
  };
  
  // const handleChangeUserType = () => {
  //   handleMenuClose();
  //   setUserTypeModalOpen(true);
  // };

  const handleEdit = () => {
    handleMenuClose();
    setEditModalOpen(true);
  };
  
  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };
  
  const handleManagePermissions = () => {
    handleMenuClose();
    // Updated route: simplified path (Phase 5: RBAC)
    history.push(`/permissions/assign/${selectedUser._id}`);
  };
  
  const handlePageChange = (event, newPage) => {
    dispatch(fetchCompanyUsers({ page: newPage + 1, limit }));
  };
  
  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    dispatch(fetchCompanyUsers({ page: 1, limit: newLimit }));
  };
  
  const getUserTypeLabel = (userType) => {
    if (userType === 'company_owner') return 'Şirket Sahibi';
    if (userType === 'company_member') return 'Şirket Üyesi';
    if (userType === 'individual') return 'Bireysel';
    if (userType === 'system_admin') return 'Sistem Yöneticisi';
    return userType;
  };
  
  const getUserTypeColor = (userType) => {
    if (userType === 'company_owner') return 'primary';
    if (userType === 'system_admin') return 'error';
    return 'default';
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (users.length === 0) {
    return (
      <Paper sx={{ p: 8, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Henüz kullanıcı yok
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
          &quot;Kullanıcı Davet Et&quot; butonunu kullanarak yeni kullanıcılar ekleyin
        </Typography>
      </Paper>
    );
  }
  
  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Kullanıcı</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Kayıt Tarihi</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user._id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar>{user.firstName?.charAt(0)}</Avatar>
                    <Typography variant="body2">
                      {user.firstName} {user.lastName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={getUserTypeLabel(user.userType)}
                    size="small"
                    color={getUserTypeColor(user.userType)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.isActive ? 'Aktif' : 'Pasif'}
                    size="small"
                    color={user.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  {new Date(user.createdAt).toLocaleDateString('tr-TR')}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={(e) => handleMenuOpen(e, user)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={limit}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage="Sayfa başına:"
        />
      </TableContainer>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>Detayları Görüntüle</MenuItem>
        
        <PermissionGate permission="users.permissions.manage">
          <MenuItem onClick={handleManagePermissions}>🔐 Yetkileri Yönet</MenuItem>
        </PermissionGate>
        
        <PermissionGate permission="users.update">
          <MenuItem onClick={handleEdit}>Düzenle</MenuItem>
        </PermissionGate>

        {/* Tip Değiştir kaldırıldı */}
        
        <PermissionGate permission="users.delete">
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            Kullanıcıyı Sil
          </MenuItem>
        </PermissionGate>
      </Menu>
      
      {/* Modals */}
      {selectedUser && (
        <>
          <UserDetailsModal
            open={detailsModalOpen}
            onClose={() => setDetailsModalOpen(false)}
            user={selectedUser}
          />
          {/* UserTypeModal kaldırıldı */}
          <DeleteUserDialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            user={selectedUser}
          />
          <UserFormModal
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            mode="edit"
            initialUser={selectedUser}
            onSuccess={() => {
              dispatch(fetchCompanyUsers({ page, limit }));
            }}
          />
        </>
      )}
    </>
  );
};

UserTable.propTypes = {
  users: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired
};

export default UserTable;

